// src/components/Campaigns.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Container, Typography, TextField, Button, Divider,
  Slider, Chip, IconButton, Alert, Tooltip, CircularProgress,
  ToggleButton, ToggleButtonGroup,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import CampaignIcon from '@mui/icons-material/Campaign';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockIcon from '@mui/icons-material/Lock';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import EditIcon from '@mui/icons-material/Edit';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TopBar from './TopBar';
import { RoundedCard } from './shared/StyledComponents';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  backgroundColor: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(4),
  },
}));

const SectionCard = styled(RoundedCard)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid #e8e8e8',
  borderRadius: 20,
  padding: theme.spacing(3),
  animation: `${fadeIn} 0.4s ease-out`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
  },
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: 'all 0.2s ease',
    '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' },
    '&.Mui-focused': { boxShadow: '0 4px 12px rgba(255, 183, 27, 0.15)' },
  },
}));

const PrimaryButton = styled(Button)(() => ({
  borderRadius: 14,
  textTransform: 'none',
  fontWeight: 700,
  padding: '12px 28px',
  fontSize: '0.95rem',
  background: 'linear-gradient(135deg, #ffb71b 0%, #ff9500 100%)',
  color: '#000',
  boxShadow: '0 4px 16px rgba(255, 183, 27, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #ffc94d 0%, #ffb71b 100%)',
    boxShadow: '0 6px 24px rgba(255, 183, 27, 0.4)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    background: '#eee',
    color: '#aaa',
    boxShadow: 'none',
  },
  transition: 'all 0.2s ease',
}));

const TypeCard = styled(Box)(({ selected, locked }) => ({
  flex: 1,
  padding: 24,
  borderRadius: 20,
  border: selected ? '2px solid #ffb71b' : '1px solid #e8e8e8',
  background: selected ? 'linear-gradient(135deg, #fffdf5 0%, #fff8e7 100%)' : '#ffffff',
  cursor: locked ? 'not-allowed' : 'pointer',
  opacity: locked ? 0.7 : 1,
  transition: 'all 0.2s ease',
  position: 'relative',
  '&:hover': locked ? {} : {
    borderColor: '#ffb71b',
    boxShadow: '0 6px 20px rgba(255, 183, 27, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const ageMarks = [
  { value: 10, label: '10' },
  { value: 30, label: '30' },
  { value: 50, label: '50' },
  { value: 80, label: '80+' },
];

function audienceLabel(f) {
  const g = f.gender === 'any' ? 'All genders' : f.gender;
  return `${g} • Age ${f.ageMin}–${f.ageMax} • ${f.radiusKm}km`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function Campaigns() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [type, setType] = useState('regular');
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [gender, setGender] = useState('any');
  const [ageRange, setAgeRange] = useState([18, 65]);
  const [radiusKm, setRadiusKm] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Reach estimate
  const [estimatedReach, setEstimatedReach] = useState(0);
  const [estimating, setEstimating] = useState(false);

  // Credits gating: 1 targeted customer = 1 credit
  const [credits, setCredits] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogPhase, setDialogPhase] = useState('confirm'); // 'confirm' | 'result'
  const [launchResult, setLaunchResult] = useState(null); // { ok, sent, creditsRemaining, message }

  const cost = estimatedReach;
  const canAfford = credits >= cost;

  const filters = useMemo(() => ({
    gender,
    ageMin: ageRange[0],
    ageMax: ageRange[1],
    radiusKm,
  }), [gender, ageRange, radiusKm]);

  const loadCampaigns = useCallback(async () => {
    if (!userId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/campaigns/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.campaigns) setCampaigns(data.campaigns);
    } catch (e) { console.error(e); }
  }, [userId]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // Debounced reach estimate
  useEffect(() => {
    if (!userId) return;
    setEstimating(true);
    const handle = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          gender: filters.gender,
          ageMin: filters.ageMin,
          ageMax: filters.ageMax,
          radiusKm: filters.radiusKm,
        });
        const res = await fetch(`/api/campaigns/${userId}/estimate?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data?.ok) {
          setEstimatedReach(data.reach);
          if (typeof data.credits === 'number') setCredits(data.credits);
        }
      } catch (e) { console.error(e); }
      finally { setEstimating(false); }
    }, 400);
    return () => clearTimeout(handle);
  }, [userId, filters]);

  const resetForm = () => {
    setTitle('');
    setMessage('');
    setGender('any');
    setAgeRange([18, 65]);
    setRadiusKm(10);
    setStartDate('');
    setEndDate('');
  };

  // Step 1: validate, then open the confirmation dialog showing the credit cost.
  const handleLaunch = (e) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) {
      setError('Campaign title is required.');
      return;
    }
    if (cost === 0) {
      setError('No customers match your audience, so there is nobody to send to.');
      return;
    }
    setDialogPhase('confirm');
    setLaunchResult(null);
    setConfirmOpen(true);
  };

  // Step 2: user confirmed — actually launch (server deducts credits + sends).
  const performLaunch = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/campaigns/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'regular',
          title,
          message,
          filters,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setDialogPhase('result');
        setLaunchResult({ ok: false, message: data?.message || 'Failed to launch campaign.' });
        if (typeof data?.credits === 'number') setCredits(data.credits);
        return;
      }
      setCampaigns(prev => [data.campaign, ...prev]);
      if (typeof data.creditsRemaining === 'number') setCredits(data.creditsRemaining);
      setDialogPhase('result');
      setLaunchResult({ ok: true, sent: data.sent, creditsRemaining: data.creditsRemaining });
      resetForm();
    } catch (err) {
      setDialogPhase('result');
      setLaunchResult({ ok: false, message: err.message || 'Something went wrong.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Editing a paused campaign ──
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: '', message: '', endDate: '' });
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (c) => {
    setEditing(c);
    setEditDraft({
      title: c.title || '',
      message: c.message || '',
      endDate: c.endDate ? new Date(c.endDate).toISOString().slice(0, 10) : '',
    });
    setEditError('');
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditing(null);
    setEditError('');
  };

  const saveEdit = async () => {
    if (!editDraft.title.trim()) { setEditError('Campaign title is required.'); return; }
    setEditSaving(true);
    setEditError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/campaigns/${userId}/${editing._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: editDraft.title.trim(),
          message: editDraft.message.trim(),
          endDate: editDraft.endDate || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || 'Could not save the campaign.');

      setCampaigns((prev) => prev.map((c) => (c._id === data.campaign._id ? data.campaign : c)));
      setEditing(null);
    } catch (err) {
      setEditError(err.message || 'Could not save the campaign.');
    } finally {
      setEditSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    const next = c.status === 'active' ? 'paused' : 'active';
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/campaigns/${userId}/${c._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: next }),
      });
      const data = await res.json();
      if (data?.ok) setCampaigns(prev => prev.map(x => x._id === c._id ? data.campaign : x));
    } catch (e) { console.error(e); }
  };

  const deleteCampaign = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/campaigns/${userId}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaigns(prev => prev.filter(c => c._id !== id));
    } catch (e) { console.error(e); }
  };

  const activeCount = campaigns.filter(c => c.status === 'active').length;

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 800, color: '#111', mb: 0.5 }}>
            Campaigns
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Reach your customers with targeted promotions and announcements.
          </Typography>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Type selector */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <TypeCard selected={type === 'regular'} onClick={() => setType('regular')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 12,
                background: 'linear-gradient(135deg, #ffb71b 0%, #ff9500 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CampaignIcon sx={{ color: '#000' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>One-Off</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
              Hand-crafted campaigns with custom audience targeting by gender, age, and location radius.
            </Typography>
          </TypeCard>

          <TypeCard selected={type === 'ai_automated'} locked onClick={() => {}}>
            <Box sx={{
              position: 'absolute', top: 14, right: 14,
              background: '#1a1a2e', color: '#fff',
              px: 1.5, py: 0.4, borderRadius: 6,
              fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5,
            }}>COMING SOON</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 12,
                background: 'linear-gradient(135deg, #b8b8d0 0%, #9090a8 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
              }}>
                <AutoAwesomeIcon sx={{ color: '#fff' }} />
                <LockIcon sx={{ position: 'absolute', bottom: -4, right: -4, fontSize: 16, color: '#666', background: '#fff', borderRadius: '50%', p: 0.2 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#666' }}>AI Automated</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#888', lineHeight: 1.6 }}>
              Let AI write, target, and schedule campaigns based on your brand story and customer behavior.
            </Typography>
          </TypeCard>
        </Box>

        {/* Body content */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          gap: 3,
          mb: 4,
        }}>
          {/* Create form */}
          <SectionCard component="form" onSubmit={handleLaunch}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 14,
                background: 'linear-gradient(135deg, #fff8e7 0%, #fff3d6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <RocketLaunchIcon sx={{ color: '#ffb71b' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Create Campaign</Typography>
                <Typography variant="body2" sx={{ color: '#888' }}>Define your audience and message</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <StyledTextField
              label="Campaign Title"
              fullWidth
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Summer Special — 20% Off Coffee"
              sx={{ mb: 2.5 }}
            />
            <StyledTextField
              label="Message"
              fullWidth
              multiline
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What do you want to say to your customers?"
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#444', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.6 }}>
              Audience
            </Typography>

            <Typography variant="body2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>Gender</Typography>
            <ToggleButtonGroup
              exclusive
              value={gender}
              onChange={(_, v) => v && setGender(v)}
              fullWidth
              sx={{ mb: 3, '& .MuiToggleButton-root': { borderRadius: 12, textTransform: 'none', fontWeight: 600, py: 1 } }}
            >
              <ToggleButton value="any">All</ToggleButton>
              <ToggleButton value="Male">Male</ToggleButton>
              <ToggleButton value="Female">Female</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>Age Range</Typography>
                <Typography variant="body2" sx={{ color: '#222', fontWeight: 700 }}>{ageRange[0]} – {ageRange[1]}</Typography>
              </Box>
              <Slider
                value={ageRange}
                onChange={(_, v) => setAgeRange(v)}
                valueLabelDisplay="auto"
                min={10}
                max={80}
                marks={ageMarks}
                sx={{ color: '#ffb71b', '& .MuiSlider-thumb': { backgroundColor: '#ffb71b' } }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>Location Radius</Typography>
                <Typography variant="body2" sx={{ color: '#222', fontWeight: 700 }}>{radiusKm} km</Typography>
              </Box>
              <Slider
                value={radiusKm}
                onChange={(_, v) => setRadiusKm(v)}
                valueLabelDisplay="auto"
                min={1}
                max={50}
                step={1}
                marks={[{ value: 1, label: '1' }, { value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }]}
                sx={{ color: '#ffb71b', '& .MuiSlider-thumb': { backgroundColor: '#ffb71b' } }}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#444', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.6 }}>
              Schedule (optional)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <StyledTextField
                label="Start"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <StyledTextField
                label="End"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
            </Box>

            <PrimaryButton type="submit" fullWidth disabled={submitting || !title.trim() || estimating || cost === 0}>
              {cost > 0 ? `Launch Campaign · ${cost} credit${cost === 1 ? '' : 's'}` : 'Launch Campaign'}
            </PrimaryButton>
          </SectionCard>

          {/* Reach estimate */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <SectionCard sx={{ background: 'linear-gradient(135deg, #fffdf5 0%, #fff8e7 100%)', border: '1px solid #ffe0a0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <PeopleAltIcon sx={{ color: '#ff9500' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#222' }}>Estimated Reach</Typography>
              </Box>

              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: '3.5rem', color: '#1a1a2e', lineHeight: 1 }}>
                    {estimating ? <CircularProgress size={32} sx={{ color: '#ffb71b' }} /> : estimatedReach}
                  </Typography>
                  <Typography sx={{ color: '#888', fontWeight: 600 }}>customers</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1 }}>
                  matching your audience filters
                </Typography>
              </Box>

              {/* Credit cost */}
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #ffe0a0', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <MonetizationOnIcon sx={{ color: '#ff9500', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#333' }}>Cost</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#1a1a2e' }}>
                    {estimating ? '—' : `${cost} credit${cost === 1 ? '' : 's'}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#888' }}>Your balance</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: canAfford ? '#16a34a' : '#e53935' }}>
                    {credits} credit{credits === 1 ? '' : 's'}
                  </Typography>
                </Box>
                {!estimating && cost > 0 && !canAfford && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#e53935', mt: 0.5 }}>
                    You need {cost - credits} more. <Box component="span" onClick={() => navigate(`/credits/${userId}`)} sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }}>Buy credits</Box>
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                Active Filters
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                <Chip size="small" label={gender === 'any' ? 'All genders' : gender} sx={{ bgcolor: '#fff', fontWeight: 600 }} />
                <Chip size="small" label={`Age ${ageRange[0]}–${ageRange[1]}`} sx={{ bgcolor: '#fff', fontWeight: 600 }} />
                <Chip size="small" label={`${radiusKm}km radius`} sx={{ bgcolor: '#fff', fontWeight: 600 }} />
              </Box>
            </SectionCard>

            <SectionCard>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#222', mb: 1 }}>Quick Stats</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>Active campaigns</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#222' }}>{activeCount}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>Total campaigns</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#222' }}>{campaigns.length}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>Total reach</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#222' }}>
                  {campaigns.reduce((s, c) => s + (c.reach || 0), 0)}
                </Typography>
              </Box>
            </SectionCard>
          </Box>
        </Box>

        {/* Campaigns list */}
        <SectionCard>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 14,
              background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CampaignIcon sx={{ color: '#5b6cff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>Your Campaigns</Typography>
              <Typography variant="body2" sx={{ color: '#888' }}>View reach and manage active campaigns</Typography>
            </Box>
            {campaigns.length > 0 && (
              <Chip label={`${campaigns.length} total`} size="small" sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }} />
            )}
          </Box>

          {campaigns.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CampaignIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
              <Typography variant="body1" sx={{ color: '#999', mb: 0.5 }}>No campaigns yet</Typography>
              <Typography variant="body2" sx={{ color: '#bbb' }}>Launch your first campaign to start reaching customers</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto', borderRadius: 12, border: '1px solid #f0f0f0' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    <HeadCell>Campaign</HeadCell>
                    <HeadCell>Audience</HeadCell>
                    <HeadCell align="right">Reach</HeadCell>
                    <HeadCell>Schedule</HeadCell>
                    <HeadCell>Status</HeadCell>
                    <HeadCell align="right">Actions</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((c) => (
                    <TableRow key={c._id} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                      <BodyCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#222' }}>{c.title}</Typography>
                        {c.message && (
                          <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.3, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.message}
                          </Typography>
                        )}
                      </BodyCell>
                      <BodyCell>
                        <Typography variant="caption" sx={{ color: '#666' }}>{audienceLabel(c.filters || {})}</Typography>
                      </BodyCell>
                      <BodyCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>{c.reach || 0}</Typography>
                      </BodyCell>
                      <BodyCell>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          {formatDate(c.startDate)}{c.endDate ? ` – ${formatDate(c.endDate)}` : ''}
                        </Typography>
                      </BodyCell>
                      <BodyCell>
                        <Chip
                          label={c.status}
                          size="small"
                          sx={{
                            backgroundColor:
                              c.status === 'active' ? '#e8f5e9' :
                              c.status === 'paused' ? '#fff3e0' :
                              '#f5f5f5',
                            color:
                              c.status === 'active' ? '#2e7d32' :
                              c.status === 'paused' ? '#e65100' :
                              '#999',
                            fontWeight: 700, fontSize: '0.7rem', textTransform: 'capitalize',
                          }}
                        />
                      </BodyCell>
                      <BodyCell align="right">
                        <Tooltip title={c.status === 'active' ? 'Pause' : 'Resume'}>
                          <IconButton size="small" onClick={() => toggleStatus(c)} sx={{ color: '#888', '&:hover': { color: '#ffb71b' } }}>
                            {c.status === 'active' ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={c.status === 'paused' ? 'Edit' : 'Pause the campaign to edit it'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={c.status !== 'paused'}
                              onClick={() => openEdit(c)}
                              sx={{ color: '#888', '&:hover': { color: '#ffb71b' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => deleteCampaign(c._id)} sx={{ color: '#ccc', '&:hover': { color: '#f44336' } }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </BodyCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </SectionCard>
      </ContentContainer>
      {/* Edit a paused campaign */}
      <Dialog open={!!editing} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit campaign</DialogTitle>
        <DialogContent>
          {/* Campaigns deliver once, at launch. Editing updates the record for
              your own reference — it cannot change messages already received. */}
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            This campaign was already delivered to its audience. Editing updates your
            records only &mdash; customers who received it will not see the changes.
          </Alert>

          {editError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{editError}</Alert>}

          <TextField
            fullWidth
            label="Campaign title"
            value={editDraft.title}
            onChange={(e) => { setEditDraft((d) => ({ ...d, title: e.target.value })); setEditError(''); }}
            size="small"
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            fullWidth
            label="Message"
            value={editDraft.message}
            onChange={(e) => setEditDraft((d) => ({ ...d, message: e.target.value }))}
            size="small"
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="End date"
            type="date"
            value={editDraft.endDate}
            onChange={(e) => setEditDraft((d) => ({ ...d, endDate: e.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <Typography variant="caption" sx={{ display: 'block', color: '#999', mt: 2 }}>
            Audience, reach and credits spent can&rsquo;t be changed &mdash; they record what
            was actually sent and charged.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} disabled={editSaving} sx={{ textTransform: 'none', color: '#777' }}>
            Cancel
          </Button>
          <Button
            onClick={saveEdit}
            disabled={editSaving || !editDraft.title.trim()}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {editSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Confirm cost → deduct → result */}
      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3, maxWidth: 440 } }}>
        {dialogPhase === 'confirm' ? (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>Send this campaign?</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: '#444', mb: 2 }}>
                This will send “{title.trim() || 'your campaign'}” to <strong>{cost}</strong> customer{cost === 1 ? '' : 's'} matching your audience.
              </DialogContentText>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', border: '1px solid #eee' }}>
                <Row label="Cost" value={`${cost} credit${cost === 1 ? '' : 's'}`} bold />
                <Row label="Your balance" value={`${credits} credit${credits === 1 ? '' : 's'}`} />
                <Divider sx={{ my: 1 }} />
                <Row label="Balance after" value={canAfford ? `${credits - cost} credit${credits - cost === 1 ? '' : 's'}` : '—'} color={canAfford ? '#16a34a' : '#e53935'} bold />
              </Box>
              {!canAfford && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  Not enough credits — you need {cost - credits} more.
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setConfirmOpen(false)} disabled={submitting} sx={{ textTransform: 'none', color: '#666' }}>
                Cancel
              </Button>
              {canAfford ? (
                <Button onClick={performLaunch} disabled={submitting} variant="contained"
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#2a2a4e' } }}>
                  {submitting ? 'Sending…' : `Confirm & deduct ${cost} credit${cost === 1 ? '' : 's'}`}
                </Button>
              ) : (
                <Button onClick={() => navigate(`/credits/${userId}`)} variant="contained"
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#ffb31b', color: '#000', '&:hover': { bgcolor: '#e6a118' } }}>
                  Buy credits
                </Button>
              )}
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
              {launchResult?.ok ? (
                <>
                  <CheckCircleIcon sx={{ fontSize: 56, color: '#22c55e', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Campaign sent</Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    Delivered to <strong>{launchResult.sent}</strong> customer{launchResult.sent === 1 ? '' : 's'}.
                    {' '}{launchResult.sent} credit{launchResult.sent === 1 ? '' : 's'} deducted — {launchResult.creditsRemaining} remaining.
                  </Typography>
                </>
              ) : (
                <>
                  <ErrorOutlineIcon sx={{ fontSize: 56, color: '#e53935', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Couldn't send</Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>{launchResult?.message}</Typography>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
              {launchResult?.ok === false && /credit/i.test(launchResult?.message || '') && (
                <Button onClick={() => { setConfirmOpen(false); navigate(`/credits/${userId}`); }}
                  sx={{ textTransform: 'none', fontWeight: 700, color: '#b27a00' }}>
                  Buy credits
                </Button>
              )}
              <Button onClick={() => setConfirmOpen(false)} variant="contained"
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#2a2a4e' } }}>
                Done
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </PageContainer>
  );
}

// Small labeled row used inside the confirm dialog's cost breakdown.
function Row({ label, value, bold, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
      <Typography variant="body2" sx={{ color: '#666' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 800 : 600, color: color || '#1a1a2e' }}>{value}</Typography>
    </Box>
  );
}

const HeadCell = styled(TableCell)(() => ({
  borderBottom: '2px solid #f0f0f0',
  padding: '12px 16px',
  fontWeight: 700,
  fontSize: '0.75rem',
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}));

const BodyCell = styled(TableCell)(() => ({
  borderBottom: '1px solid #f5f5f5',
  padding: '12px 16px',
  fontSize: '0.875rem',
}));
